import React from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import {
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  GridComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import './TrendChart.scss'; // optional stylesheet
import style from './TrendChart.scss.json';

// Register only the features we actually need from ECharts:
echarts.use([TitleComponent, TooltipComponent, LegendComponent, LineChart, GridComponent, CanvasRenderer]);

/**
 * We assume your `trendReports` data is shaped like this:
 * [
 *   { hour: 0, clicks: 100, sales: 5, cvr: 0.05 },  // cvr is e.g. 0.05 => 5%
 *   { hour: 1, clicks: 120, sales: 6, cvr: 0.05 },
 *   ...
 * ]
 */
interface TrendChartsProps {
  data: Array<{
    hour: number;
    clicks: number;
    sales: number;
    cvr: number | null;
  }>;
}

export const TrendChart: React.FC<TrendChartsProps> = ({ data }) => {
  // Prepare arrays for chart axes
  const hours = data.map((item) => item.hour) || [];
  const cvrValues = data.map((item) =>
    item.cvr !== null ? Math.round(item.cvr * 100) : 0
  );
  const clicksValues = data.map((item) => item.clicks);
  const salesValues = data.map((item) => item.sales);

  // ECharts options for the CVR Graph
  const cvrOption = {
    title: {
      text: 'CVR per hour',
      left: 'center',
    },
    tooltip: {
      trigger: 'axis',
    },
    xAxis: [{
      type: 'category',
      data: hours,
      name: 'Hour',
    }],
    yAxis: [{
      type: 'value',
      name: 'CVR (%)',
      axisLabel: {
        formatter: '{value}%',
      },
    }],
    series: [
      {
        name: 'CVR',
        data: cvrValues,
        type: 'line',
        smooth: true,
        // xAxisIndex defaults to 0 which will now be found in the array
      },
    ],
  };
  

  // ECharts options for the Clicks Graph
  const clicksOption = {
    title: {
      text: 'Clicks per hour',
      left: 'center',
    },
    tooltip: {
      trigger: 'axis',
    },
    xAxis: [{
      type: 'category',
      data: hours,
      name: 'Hour',
    }],
    yAxis: [{
      type: 'value',
      name: 'Clicks',
    }],
    series: [
      {
        name: 'Clicks',
        data: clicksValues,
        type: 'line',
        smooth: true,
      },
    ],
  };

  // ECharts options for the Sales Graph
  const salesOption = {
    title: {
      text: 'Sales per hour',
      left: 'center',
    },
    tooltip: {
      trigger: 'axis',
    },
    xAxis: [{
      type: 'category',
      data: hours,
      name: 'Hour',
    }],
    yAxis: [{
      type: 'value',
      name: 'Sales',
    }],
    series: [
      {
        name: 'Sales',
        data: salesValues,
        type: 'line',
        smooth: true,
      },
    ],
  };

  return (
    <div className={style.trendChartContainer}>
      <div className={style.chartWrapper}>
        <ReactEChartsCore
          echarts={echarts}
          option={cvrOption}
          notMerge={true}
          lazyUpdate={true}
          style={{ width: '450px', height: '300px' }}
        />
      </div>
      <div className={style.chartWrapper}>
        <ReactEChartsCore
          echarts={echarts}
          option={clicksOption}
          notMerge={true}
          lazyUpdate={true}
          style={{ width: '450px', height: '300px' }}
        />
      </div>
      <div className={style.chartWrapper}>
        <ReactEChartsCore
          echarts={echarts}
          option={salesOption}
          notMerge={true}
          lazyUpdate={true}
          style={{ width: '450px', height: '300px' }}
        />
      </div>
    </div>
  );
};
